import React, { useRef, useState } from 'react';
import { Avatar, Box, Button, CircularProgress, FormLabel, Modal, Stack } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AvatarEditor from 'react-avatar-editor';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';

/**
 * A round photo that picks a new one when clicked: the admin chooses a .jpg or .png, crops
 * and zooms it, and onCrop gets it as a JPEG blob and a data URL (to preview until it's
 * uploaded). Uploading is the caller's; isUploading shows a spinner over the photo.
 */
export const AvatarImagePicker = ({
	imageUrl = '',
	disabled = false,
	isUploading = false,
	size = 200,
	onCrop,
}) => {
	const dispatch = useDispatch();
	const editorRef = useRef(null);
	const [editorOpen, setEditorOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [scale, setScale] = useState(1);

	const handleImageSelect = (e) => {
		const file = e.target.files?.[0];
		// Cleared so choosing the same file again still opens the editor
		e.target.value = '';
		if (!file) return;

		const fileExtension = file.name.split('.').pop().toLowerCase();
		if (fileExtension !== 'jpg' && fileExtension !== 'jpeg' && fileExtension !== 'png') {
			dispatch(showToast({ message: 'Please upload a .jpg or .png file' }));
			return;
		}

		setSelectedImage(file);
		setScale(1);
		setEditorOpen(true);
	};

	const handleSaveCrop = () => {
		if (!editorRef.current) return;

		const canvas = editorRef.current.getImageScaledToCanvas();
		canvas.toBlob(
			(blob) => {
				if (!blob) return;
				setEditorOpen(false);
				onCrop(blob, canvas.toDataURL('image/jpeg', 0.9));
			},
			'image/jpeg',
			0.9,
		);
	};

	return (
		<>
			<Box
				sx={{
					position: 'relative',
					width: size,
					height: size,
					flexShrink: 0,
					...(!disabled && { cursor: 'pointer' }),
				}}
			>
				<label
					style={{
						position: 'relative',
						display: 'block',
						width: '100%',
						height: '100%',
						pointerEvents: disabled ? 'none' : 'auto',
					}}
				>
					<Avatar
						src={imageUrl || ''}
						alt='Profile Picture'
						sx={{
							width: '100%',
							height: '100%',
							backgroundColor: '#999999',
							boxShadow: 2,
						}}
					>
						{!imageUrl && 'Logo'}
					</Avatar>

					{!disabled && !isUploading && (
						<input
							type='file'
							hidden
							accept='image/png, image/jpeg'
							onChange={handleImageSelect}
						/>
					)}

					{!disabled && (
						<Box
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								width: '100%',
								height: '100%',
								borderRadius: '50%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								transition: 'backgroundColor 0.3s ease',
								...(!isUploading && { cursor: 'pointer' }),
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.52)',
								},
								color: 'white',
							}}
						>
							{isUploading ? (
								<CircularProgress color='inherit' sx={{ opacity: 1, zIndex: 999 }} />
							) : (
								<Box
									display='flex'
									justifyContent='center'
									alignItems='center'
									sx={{
										width: '100%',
										height: '100%',
										opacity: 0,
										transition: 'opacity 0.3s ease',
										'&:hover': {
											opacity: 1,
										},
									}}
								>
									<CloudUploadIcon
										fontSize={size >= 120 ? 'large' : 'medium'}
										sx={{ color: 'white' }}
									/>
								</Box>
							)}
						</Box>
					)}
				</label>
			</Box>

			<Modal
				open={editorOpen}
				onClose={() => setEditorOpen(false)}
				aria-labelledby='avatar-editor-modal'
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: 400,
						bgcolor: 'background.paper',
						boxShadow: 24,
						p: 4,
						outline: 'none',
					}}
				>
					<AvatarEditor
						ref={editorRef}
						image={selectedImage}
						width={400}
						height={400}
						border={50}
						scale={scale}
						color={[255, 255, 255, 0.6]}
						style={{ width: '100%', height: 'auto' }}
					/>
					<Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
						<FormLabel>Zoom</FormLabel>
						<input
							type='range'
							min='1'
							max='3'
							step='0.1'
							value={scale}
							onChange={(e) => setScale(parseFloat(e.target.value))}
							style={{ width: '100%' }}
						/>
						<Stack direction='row' spacing={2} justifyContent='flex-end'>
							<Button variant='outlined' onClick={() => setEditorOpen(false)}>
								Cancel
							</Button>
							<Button variant='contained' onClick={handleSaveCrop}>
								Save
							</Button>
						</Stack>
					</Box>
				</Box>
			</Modal>
		</>
	);
};
